import { logDataDogError } from '../../../../../errors/utils/errors'
import { FetchApiClient } from '../fetch-api-client'

// The API response is directly a mapping of user IDs to avatar URLs
type AvatarResponse = Record<string, string>
export type AvatarSize = 'thumbnail' | 'large'

interface AvatarCacheEntry {
  url: string | null // null means no avatar found
  expiresAt: number // epoch ms; the signed URL is unusable past this
}

interface AvatarCache {
  [key: string]: AvatarCacheEntry // key is `${size}:${userId}`
}

/**
 * Avatar URLs are signed and expire 15 minutes after they're issued. Entries are
 * dropped at 10 so a URL handed out just before eviction is still valid by the
 * time the browser requests the image.
 */
const CACHE_TTL_MS = 10 * 60 * 1000

/**
 * The cache is mirrored to sessionStorage so a page refresh reuses the URLs it
 * already has. Re-issuing them is not just an extra API call: each signing
 * produces a different URL, so it's a new browser-cache key and the image bytes
 * get downloaded again even though nothing about the avatar changed.
 */
const STORAGE_KEY = 'skedulo:avatar-cache:v1'

/** sessionStorage, or null where it's unavailable (SSR) or blocked (cookies off). */
function getSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    // Reading the global itself throws when storage is blocked by policy.
    return null
  }
}

function isLiveEntry(entry: unknown, now: number): entry is AvatarCacheEntry {
  if (typeof entry !== 'object' || entry === null) return false
  const { url, expiresAt } = entry as Partial<AvatarCacheEntry>
  if (typeof expiresAt !== 'number' || expiresAt <= now) return false
  return url === null || typeof url === 'string'
}

export class AvatarApiClient {
  private cache: AvatarCache = {}
  private pendingRequests = new Set<string>()
  private batchQueue: Array<{ userId: string; size: AvatarSize }> = []
  private batchTimeout: ReturnType<typeof setTimeout> | null = null
  private readonly BATCH_DELAY = 100 // ms to wait before sending batch request
  private readonly BATCH_SIZE = 50 // max user IDs per batch

  constructor(protected readonly client: FetchApiClient) {
    this.cache = this.readPersistedCache()
  }

  /** Hydrate from sessionStorage, dropping anything already expired. */
  private readPersistedCache(): AvatarCache {
    const storage = getSessionStorage()
    if (!storage) return {}

    try {
      const raw = storage.getItem(STORAGE_KEY)
      if (!raw) return {}

      const now = Date.now()
      const live: AvatarCache = {}
      Object.entries(JSON.parse(raw) as Record<string, unknown>).forEach(
        ([key, entry]) => {
          if (isLiveEntry(entry, now)) live[key] = entry
        },
      )
      return live
    } catch {
      // Unparseable or unreadable — start cold rather than break avatars.
      return {}
    }
  }

  private persistCache(): void {
    const storage = getSessionStorage()
    if (!storage) return

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(this.cache))
    } catch {
      // Over quota or storage disabled mid-session: drop the stale key so a
      // later refresh doesn't hydrate a half-written cache, and carry on in
      // memory. Avatars still work, they just don't survive the refresh.
      try {
        storage.removeItem(STORAGE_KEY)
      } catch {
        // Nothing further we can do.
      }
    }
  }

  /** The entry for `key`, or undefined when absent or expired (expired is evicted). */
  private getLiveEntry(key: string): AvatarCacheEntry | undefined {
    const entry = this.cache[key]
    if (isLiveEntry(entry, Date.now())) return entry
    if (entry !== undefined) delete this.cache[key]
    return undefined
  }

  private setEntry(key: string, url: string | null): void {
    this.cache[key] = { url, expiresAt: Date.now() + CACHE_TTL_MS }
  }

  /**
   * Get avatar URLs for multiple user IDs
   * Uses caching and batching to optimize API calls
   */
  async getAvatars(
    userIds: string[],
    size: AvatarSize = 'large',
  ): Promise<Record<string, string | null>> {
    const uncached: Array<{ userId: string; size: AvatarSize }> = []

    // Check cache first
    for (const userId of userIds) {
      const key = this.makeKey(userId, size)
      if (!this.getLiveEntry(key) && !this.pendingRequests.has(key)) {
        uncached.push({ userId, size })
      }
    }

    // If we have uncached IDs, add them to batch queue
    if (uncached.length > 0) {
      // In here we will add the uncached items to the batch queue and wait for the pending requests to complete
      // After that we will add the results to the cache
      this.addToBatchQueue(uncached)
    }

    // Wait for any pending requests to complete
    await this.waitForPendingRequests(userIds, size)

    // Return final results from cache after pending requests are complete
    const result: Record<string, string | null> = {}
    for (const userId of userIds) {
      const entry = this.getLiveEntry(this.makeKey(userId, size))
      result[userId] = entry ? entry.url : null
    }

    return result
  }

  private addToBatchQueue(
    items: Array<{ userId: string; size: AvatarSize }>,
  ): void {
    // Add to queue and mark as pending
    items.forEach(({ userId, size }) => {
      const key = this.makeKey(userId, size)
      const exists = this.batchQueue.some(
        (q) => q.userId === userId && q.size === size,
      )
      if (!exists && !this.pendingRequests.has(key)) {
        this.batchQueue.push({ userId, size })
        this.pendingRequests.add(key)
      }
    })

    // Clear existing timeout and set new one
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout)
    }

    this.batchTimeout = setTimeout(() => {
      this.processBatchQueue()
    }, this.BATCH_DELAY)
  }

  private async processBatchQueue(): Promise<void> {
    if (this.batchQueue.length === 0) return

    // Process queue in batches, grouping by size so each request has a single size
    while (this.batchQueue.length > 0) {
      const { size } = this.batchQueue[0]
      const sameSize: Array<{ userId: string; size: AvatarSize }> = []
      for (
        let i = 0;
        i < this.batchQueue.length && sameSize.length < this.BATCH_SIZE;
      ) {
        if (this.batchQueue[i].size === size) {
          sameSize.push(this.batchQueue.splice(i, 1)[0])
        } else {
          i++
        }
      }
      await this.fetchAvatarBatch(
        sameSize.map((i) => i.userId),
        size,
      )
    }
  }

  private async fetchAvatarBatch(
    userIds: string[],
    size: AvatarSize,
  ): Promise<void> {
    try {
      const sizeHint = size === 'large' ? '' : `&size_hint=${size}`
      const apiPath = `files/avatar?user_ids=${userIds.join(',')}${sizeHint}`

      const response = await this.client.get<AvatarResponse>(apiPath)

      // The response is directly a mapping of user IDs to avatar URLs
      if (response && typeof response === 'object') {
        // Process each user ID in the response
        Object.entries(response).forEach(([userId, avatarUrl]) => {
          const key = this.makeKey(userId, size)
          const isUrl = typeof avatarUrl === 'string' && !!avatarUrl.trim()
          this.setEntry(key, isUrl ? avatarUrl : null)
          this.pendingRequests.delete(key)
        })

        // Mark any requested userIds that weren't in the response as no avatar
        userIds.forEach((userId) => {
          const key = this.makeKey(userId, size)
          if (!Object.prototype.hasOwnProperty.call(this.cache, key)) {
            this.setEntry(key, null)
          }
          this.pendingRequests.delete(key)
        })
      } else {
        // Invalid response format, mark all as no avatar
        userIds.forEach((userId) => {
          const key = this.makeKey(userId, size)
          this.setEntry(key, null)
          this.pendingRequests.delete(key)
        })
      }
    } catch (error) {
      // On error, mark all userIds as no avatar and remove from pending
      userIds.forEach((userId) => {
        const key = this.makeKey(userId, size)
        this.setEntry(key, null)
        this.pendingRequests.delete(key)
      })

      // Emit the error to the event bus using the framework signature
      logDataDogError(new Error(`Avatar Service -Avatar API failed`), {
        component: `Avatar Service`,
        description: `Avatar API failed`,
        userIds,
        size,
      })

      // Re-throw the error to be caught by the caller
      throw new Error('Failed to fetch avatars')
    } finally {
      // Runs on the throwing path too, so the negative results are cached and
      // a failed batch isn't retried on every render.
      this.persistCache()
    }
  }

  private async waitForPendingRequests(
    userIds: string[],
    size: AvatarSize,
  ): Promise<void> {
    const maxWait = 5000 // 5 seconds max wait
    const checkInterval = 50 // Check every 50ms
    let waited = 0

    while (waited < maxWait) {
      const stillPending = userIds.some((userId) =>
        this.pendingRequests.has(this.makeKey(userId, size)),
      )
      if (!stillPending) break

      await new Promise((resolve) => setTimeout(resolve, checkInterval))
      waited += checkInterval
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache = {}
    this.pendingRequests.clear()
    this.batchQueue = []
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout)
      this.batchTimeout = null
    }
    this.persistCache()
  }

  /**
   * Clear the cache for a single user
   */
  clearSingleCache(userId: string, size?: AvatarSize): void {
    const sizes = size ? [size] : (['thumbnail', 'large'] as AvatarSize[])
    sizes.forEach((s) => {
      const key = this.makeKey(userId, s)
      delete this.cache[key]
      this.pendingRequests.delete(key)
    })
    this.persistCache()
  }

  /**
   * Get current cache size (expired entries excluded)
   */
  getCacheSize(): number {
    return Object.keys(this.cache).filter((key) => this.getLiveEntry(key))
      .length
  }

  /**
   * Get cached avatar for a specific user. `undefined` means "not cached" —
   * an expired entry reads as uncached so the caller refetches rather than
   * rendering a dead signed URL.
   */
  getCachedAvatar(
    userId: string,
    size: AvatarSize = 'large',
  ): string | null | undefined {
    return this.getLiveEntry(this.makeKey(userId, size))?.url
  }

  private makeKey(userId: string, size: AvatarSize): string {
    return `${size}:${userId}`
  }
}
